/**
 * API image endpoints accept JPEG, PNG, and WebP up to 10MB.
 * Mobile (especially Samsung) often sends empty/wrong MIME types, very large JPEGs, or HEIC.
 * We decode when needed, cap long edge for canvas/mobile stability, re-encode as JPEG with a
 * correct type so multipart uploads are accepted.
 */

const MAX_LONG_EDGE_PX = 4096
/** Re-encode if larger so we stay under server limit after JPEG compression */
const MAX_OUTPUT_BYTES = 9 * 1024 * 1024

const ALLOWED = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp'])

function baseName (name: string): string {
  return name.replace(/\.[^/.]+$/, '') || 'photo'
}

function canvasToJpegFile (canvas: HTMLCanvasElement, nameBase: string, quality: number): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not convert image to JPEG'))
          return
        }
        resolve(new File([blob], `${nameBase}.jpg`, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      quality
    )
  })
}

async function decodeToBitmap (file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file)
  } catch {
    /* createImageBitmap may fail on some sources; fall back to HTMLImageElement */
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      void (async () => {
        try {
          resolve(await createImageBitmap(img))
        } catch (e) {
          reject(e instanceof Error ? e : new Error('Could not load image'))
        }
      })()
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(
        new Error(
          'Could not read this photo. If it is HEIF/HEIC, turn off “High efficiency” in the camera app or pick “JPEG” in Gallery, then try again.'
        )
      )
    }
    img.src = url
  })
}

function drawScaledToCanvas (bmp: ImageBitmap): HTMLCanvasElement {
  const w = bmp.width
  const h = bmp.height
  const scale = Math.min(1, MAX_LONG_EDGE_PX / Math.max(w, h))
  const tw = Math.max(1, Math.round(w * scale))
  const th = Math.max(1, Math.round(h * scale))
  const canvas = document.createElement('canvas')
  canvas.width = tw
  canvas.height = th
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not process image')
  ctx.drawImage(bmp, 0, 0, tw, th)
  return canvas
}

/**
 * Returns a File suitable for upload: correct image/jpeg type, dimensions bounded, size under ~9MB when possible.
 */
export async function ensureUploadableImageFile (file: File): Promise<File> {
  const nameBase = baseName(file.name)
  const t = (file.type || '').toLowerCase()
  const typeOk = ALLOWED.has(t)
  const sizeOk = file.size <= MAX_OUTPUT_BYTES

  if (typeOk && sizeOk) {
    try {
      const probe = await createImageBitmap(file)
      const fits = Math.max(probe.width, probe.height) <= MAX_LONG_EDGE_PX
      probe.close()
      if (fits) return file
    } catch {
      /* decode failed or unsupported — try full pipeline */
    }
  }

  let bmp: ImageBitmap | null = null
  try {
    bmp = await decodeToBitmap(file)
  } catch (e) {
    throw e instanceof Error ? e : new Error('Could not load image')
  }

  try {
    let canvas = drawScaledToCanvas(bmp)
    const qualities = [0.88, 0.78, 0.68, 0.58, 0.48] as const

    for (let attempt = 0; attempt < 8; attempt++) {
      for (const q of qualities) {
        const out = await canvasToJpegFile(canvas, nameBase, q)
        if (out.size <= MAX_OUTPUT_BYTES) return out
      }
      const nextW = Math.max(1, Math.round(canvas.width * 0.85))
      const nextH = Math.max(1, Math.round(canvas.height * 0.85))
      if (nextW === canvas.width && nextH === canvas.height) break
      const next = document.createElement('canvas')
      next.width = nextW
      next.height = nextH
      const ctx = next.getContext('2d')
      if (!ctx) break
      ctx.drawImage(canvas, 0, 0, nextW, nextH)
      canvas = next
    }

    throw new Error('Photo is still too large after resizing. Try a lower resolution in the camera app.')
  } finally {
    bmp.close()
  }
}
